import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Gift, Megaphone, Newspaper, Pin, StickyNote } from "lucide-react";
import { useI18n } from "../../../i18n";
import { fmtDate } from "../../../lib/format";
import { useApi } from "../hooks";

const ICON = { announcement: Megaphone, news: Newspaper, note: StickyNote };
const TONE = { announcement: "bg-navy text-white", news: "bg-vblue/10 text-vblue-ink", note: "bg-brand/25 text-navy" };

const PostCard = ({ p }) => {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const pick = (f) => p[`${f}_${lang}`] || p[`${f}_el`];
  const Icon = ICON[p.kind];
  const body = pick("body");
  const long = body.length > 180;
  return (
    <li className="rounded-2xl bg-white p-4 shadow-sm" data-testid={`home-post-${p.id}`}>
      <div className="flex items-center gap-2 text-xs">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${TONE[p.kind]}`}><Icon className="h-3.5 w-3.5" />{t(`posts.kind.${p.kind}`)}</span>
        {p.pinned && <Pin className="h-3.5 w-3.5 text-brand" />}
        <span className="ml-auto text-slate-400">{fmtDate(p.start_date || p.created_at.slice(0, 10))}</span>
      </div>
      <h3 className="mt-2 text-base font-semibold leading-snug text-navy">{pick("title")}</h3>
      {body && <p className={`mt-1 whitespace-pre-line break-words text-sm text-slate-600 ${open || !long ? "" : "line-clamp-3"}`}>{body}</p>}
      {long && <button type="button" onClick={() => setOpen(!open)} className="mt-1 h-8 text-xs font-semibold text-vblue-ink" data-testid={`home-post-more-${p.id}`}>{t(open ? "posts.less" : "posts.more")}</button>}
    </li>
  );
};

export const HomePosts = () => {
  const { t } = useI18n();
  const posts = useApi("homePosts", "/content/posts");
  if (!posts.data?.length) return null;
  return (
    <section className="space-y-3" data-testid="home-posts">
      <h2 className="text-base font-semibold text-navy md:text-lg">{t("posts.homeTitle")}</h2>
      <ul className="grid gap-3 md:grid-cols-2">{posts.data.map((p) => <PostCard key={p.id} p={p} />)}</ul>
    </section>
  );
};

export const HomeOffers = ({ promos }) => {
  const { t, lang } = useI18n();
  const offers = (promos || []).filter((p) => p.items?.length);
  if (!offers.length) return null;
  return (
    <section className="space-y-3" data-testid="home-offers">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-navy md:text-lg">{t("posts.offersTitle")}</h2>
        <Link to="/app/offers" className="flex h-10 items-center gap-1 text-sm font-semibold text-vblue-ink" data-testid="home-offers-all">{t("posts.offersAll")}<ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-1 [scrollbar-width:none]">
        {offers.slice(0, 8).map((o) => (
          <Link key={o.id} to="/app/offers" data-testid={`home-offer-${o.id}`} className="flex w-64 shrink-0 snap-start flex-col rounded-2xl bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
            <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-vblue-ink"><Gift className="h-3.5 w-3.5" />{t("posts.offersUntil", { date: fmtDate(o.end_date) })}</span>
            <p className="mt-1 line-clamp-2 font-semibold text-navy">{o[`title_${lang}`] || o.title_el}</p>
            <p className="mt-auto pt-2 text-xs text-slate-500">{t("posts.offersItems", { n: o.items.length })}</p>
          </Link>
        ))}
      </div>
    </section>
  );
};
